/**
 * Rough cost estimates for garden components (in EUR)
 * These are used for real-time budget feedback during questionnaire
 * 
 * Note: These are rough estimates - actual costs vary by:
 * - Location (regional price differences)
 * - Material quality choices
 * - Garden size
 * - Installation complexity
 * - Local contractor rates
 */

export type ComponentCostRange = {
	componentId: string;
	min: number; // Minimum cost estimate (EUR)
	mid: number; // Mid-range estimate (EUR)
	max: number; // Maximum cost estimate (EUR)
	description: string; // What affects the cost
};

/**
 * Cost estimates for each garden component
 * Based on European market averages (2024-2025)
 */
export const componentCosts: ComponentCostRange[] = [
	{
		componentId: 'pool',
		min: 15000,
		mid: 35000,
		max: 80000,
		description: 'Pool size, type (in-ground vs above-ground), materials, filtration system, heating, decking'
	},
	{
		componentId: 'fence',
		min: 20, // per meter
		mid: 50,
		max: 150,
		description: 'Material (wood, metal, vinyl), height, length, installation complexity'
	},
	{
		componentId: 'bbq',
		min: 500,
		mid: 2000,
		max: 8000,
		description: 'Type (portable vs built-in), size, materials, additional features (sink, storage)'
	},
	{
		componentId: 'lounge',
		min: 800,
		mid: 2500,
		max: 8000,
		description: 'Furniture quality, number of pieces, materials (wood, metal, wicker), cushions'
	},
	{
		componentId: 'outdoor-kitchen',
		min: 3000,
		mid: 12000,
		max: 30000,
		description: 'Size, appliances (BBQ, sink, fridge), countertop materials, storage, plumbing/electrical'
	},
	{
		componentId: 'fire-pit',
		min: 300,
		mid: 1500,
		max: 5000,
		description: 'Type (wood vs gas), size, materials (stone, metal, concrete), seating around it'
	},
	{
		componentId: 'pergola',
		min: 1500,
		mid: 5000,
		max: 15000,
		description: 'Size, materials (wood, metal, vinyl), roof type, installation complexity'
	},
	{
		componentId: 'deck',
		min: 80, // per m²
		mid: 150,
		max: 300,
		description: 'Material (wood, composite, PVC), size, height, railings, stairs'
	},
	{
		componentId: 'shed',
		min: 500,
		mid: 2000,
		max: 8000,
		description: 'Size, materials (wood, metal, plastic), foundation, windows, electrical'
	},
	{
		componentId: 'playground',
		min: 1000,
		mid: 4000,
		max: 15000,
		description: 'Equipment type and quantity, safety surfacing, installation, age-appropriateness'
	},
	{
		componentId: 'vegetable-garden',
		min: 200, // per m²
		mid: 400,
		max: 800,
		description: 'Raised beds vs in-ground, materials for beds, soil/compost, irrigation, tools'
	},
	{
		componentId: 'water-feature',
		min: 500,
		mid: 2500,
		max: 10000,
		description: 'Type (fountain, pond, waterfall), size, materials, pump system, lighting'
	},
	{
		componentId: 'lighting',
		min: 500,
		mid: 2000,
		max: 8000,
		description: 'Number of fixtures, type (solar, LED, low-voltage), installation, automation'
	},
	{
		componentId: 'pathway',
		min: 30, // per m²
		mid: 80,
		max: 200,
		description: 'Material (gravel, stone, pavers, concrete), width, length, base preparation'
	},
	{
		componentId: 'retaining-wall',
		min: 100, // per meter
		mid: 250,
		max: 600,
		description: 'Height, materials (stone, concrete blocks, wood), length, drainage, engineering'
	},
	{
		componentId: 'driveway',
		min: 40, // per m²
		mid: 80,
		max: 150,
		description: 'Material (gravel, pavers, concrete, resin), size, drainage, edging'
	},
	{
		componentId: 'greenhouse',
		min: 500,
		mid: 3000,
		max: 15000,
		description: 'Size, materials (glass, polycarbonate, aluminium), heating, ventilation, staging'
	},
	{
		componentId: 'flower-beds',
		min: 150, // per m²
		mid: 350,
		max: 700,
		description: 'Size, edging materials, soil, plants, mulch'
	},
	{
		componentId: 'hot-tub',
		min: 3000,
		mid: 8000,
		max: 20000,
		description: 'Size, type (in-ground vs above-ground), heating, jets, decking/surround'
	},
	{
		componentId: 'pizza-oven',
		min: 800,
		mid: 2500,
		max: 6000,
		description: 'Type (portable vs built-in), size, materials (brick, stone, stainless), chimney'
	},
	{
		componentId: 'compost',
		min: 50,
		mid: 200,
		max: 600,
		description: 'Type (bin, bay, tumbler), size, materials, location'
	}
];

/**
 * Base costs that apply to most projects
 */
export const baseProjectCosts = {
	design: { min: 500, mid: 1500, max: 5000 },
	sitePreparation: { min: 500, mid: 2000, max: 5000 },
	landscaping: { min: 20, mid: 50, max: 100 }, // per m²
	plants: { min: 10, mid: 30, max: 80 }, // per m²
	irrigation: { min: 1000, mid: 3000, max: 8000 },
	cleanup: { min: 300, mid: 800, max: 2000 }
};

/**
 * Get cost estimate for a component
 */
export function getComponentCost(componentId: string, gardenSize?: number): ComponentCostRange | null {
	const cost = componentCosts.find(c => c.componentId === componentId);
	if (!cost) return null;
	
	// For components priced per unit (fence, deck, pathway, retaining-wall, vegetable-garden)
	// we need garden size to estimate
	if (componentId === 'fence' || componentId === 'retaining-wall') {
		// Rough estimate: assume perimeter is ~4x the square root of area
		const perimeter = gardenSize ? Math.sqrt(gardenSize) * 4 : 40; // default 40m
		return {
			...cost,
			min: cost.min * perimeter,
			mid: cost.mid * perimeter,
			max: cost.max * perimeter,
			description: `${cost.description} (estimated for ~${Math.round(perimeter)}m)`
		};
	}
	
	if (componentId === 'deck' || componentId === 'pathway') {
		// Assume 20-30% of garden area for deck/pathway
		const area = gardenSize ? gardenSize * 0.25 : 25; // default 25m²
		return {
			...cost,
			min: cost.min * area,
			mid: cost.mid * area,
			max: cost.max * area,
			description: `${cost.description} (estimated for ~${Math.round(area)}m²)`
		};
	}
	
	if (componentId === 'vegetable-garden') {
		// Assume 10-20% of garden for vegetable garden
		const area = gardenSize ? gardenSize * 0.15 : 10; // default 10m²
		return {
			...cost,
			min: cost.min * area,
			mid: cost.mid * area,
			max: cost.max * area,
			description: `${cost.description} (estimated for ~${Math.round(area)}m²)`
		};
	}

	if (componentId === 'flower-beds') {
		// Assume 10-15% of garden for flower beds
		const area = gardenSize ? gardenSize * 0.12 : 8; // default 8m²
		return {
			...cost,
			min: cost.min * area,
			mid: cost.mid * area,
			max: cost.max * area,
			description: `${cost.description} (estimated for ~${Math.round(area)}m²)`
		};
	}

	if (componentId === 'driveway') {
		// Assume 15-25m² for driveway
		const area = gardenSize ? Math.min(25, Math.max(15, gardenSize * 0.1)) : 20;
		return {
			...cost,
			min: cost.min * area,
			mid: cost.mid * area,
			max: cost.max * area,
			description: `${cost.description} (estimated for ~${Math.round(area)}m²)`
		};
	}
	
	return cost;
}

/**
 * Calculate total estimated cost for selected components
 */
export function calculateTotalCost(
	selectedComponents: string[],
	gardenSize: number = 50
): { min: number; mid: number; max: number; breakdown: Array<{ component: string; cost: ComponentCostRange }> } {
	let totalMin = 0;
	let totalMid = 0;
	let totalMax = 0;
	const breakdown: Array<{ component: string; cost: ComponentCostRange }> = [];
	
	// Add base costs
	totalMin += baseProjectCosts.design.min + baseProjectCosts.sitePreparation.min;
	totalMid += baseProjectCosts.design.mid + baseProjectCosts.sitePreparation.mid;
	totalMax += baseProjectCosts.design.max + baseProjectCosts.sitePreparation.max;
	
	// Add landscaping and plants (based on garden size)
	totalMin += baseProjectCosts.landscaping.min * gardenSize + baseProjectCosts.plants.min * gardenSize;
	totalMid += baseProjectCosts.landscaping.mid * gardenSize + baseProjectCosts.plants.mid * gardenSize;
	totalMax += baseProjectCosts.landscaping.max * gardenSize + baseProjectCosts.plants.max * gardenSize;
	
	// Add component costs
	selectedComponents.forEach(componentId => {
		const cost = getComponentCost(componentId, gardenSize);
		if (cost) {
			totalMin += cost.min;
			totalMid += cost.mid;
			totalMax += cost.max;
			breakdown.push({ component: componentId, cost });
		}
	});
	
	// Add cleanup
	totalMin += baseProjectCosts.cleanup.min;
	totalMid += baseProjectCosts.cleanup.mid;
	totalMax += baseProjectCosts.cleanup.max;
	
	return { min: totalMin, mid: totalMid, max: totalMax, breakdown };
}

/**
 * Get budget status and suggestions
 */
export function getBudgetStatus(
	estimatedCost: { min: number; mid: number; max: number },
	budgetRange?: { min: number; max: number }
): {
	status: 'within' | 'close' | 'over' | 'way-over';
	message: string;
	suggestions: string[];
} {
	if (!budgetRange) {
		return {
			status: 'within',
			message: 'No budget specified - estimates are for reference only.',
			suggestions: []
		};
	}
	
	const budgetMid = (budgetRange.min + budgetRange.max) / 2;
	const estimatedMid = estimatedCost.mid;
	const ratio = estimatedMid / budgetMid;
	
	if (ratio <= 0.9) {
		return {
			status: 'within',
			message: `Estimated cost (€${Math.round(estimatedCost.mid).toLocaleString()}) is within your budget range (€${budgetRange.min.toLocaleString()}-€${budgetRange.max.toLocaleString()}).`,
			suggestions: []
		};
	} else if (ratio <= 1.2) {
		return {
			status: 'close',
			message: `Estimated cost (€${Math.round(estimatedCost.mid).toLocaleString()}) is close to your budget. Consider choosing mid-range materials or scaling down some features.`,
			suggestions: [
				'Choose mid-range materials instead of premium',
				'Consider smaller sizes for expensive components',
				'Phase the project - do essential features first'
			]
		};
	} else if (ratio <= 1.5) {
		return {
			status: 'over',
			message: `Estimated cost (€${Math.round(estimatedCost.mid).toLocaleString()}) exceeds your budget (€${budgetRange.min.toLocaleString()}-€${budgetRange.max.toLocaleString()}) by ~${Math.round((ratio - 1) * 100)}%.`,
			suggestions: [
				'Remove or replace expensive components (e.g., pool, outdoor kitchen)',
				'Choose budget-friendly materials',
				'Reduce garden size or component sizes',
				'Consider DIY for some components',
				'Phase the project over multiple years'
			]
		};
	} else {
		return {
			status: 'way-over',
			message: `Estimated cost (€${Math.round(estimatedCost.mid).toLocaleString()}) significantly exceeds your budget (€${budgetRange.min.toLocaleString()}-€${budgetRange.max.toLocaleString()}) by ~${Math.round((ratio - 1) * 100)}%.`,
			suggestions: [
				'Remove expensive components (pool, outdoor kitchen, pergola)',
				'Focus on essential features only',
				'Use budget materials throughout',
				'Consider a phased approach over 2-3 years',
				'Increase budget or reduce scope significantly'
			]
		};
	}
}
